import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getEducationalPosts,
  getMemberAlerts,
  getMixedPostsForPublisher,
  getPublishers,
} from "../../api";
import useGoToPricing from "../../hooks/useGoToPricing";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import AnalystProfileContent from "./AnalystProfileContent";
import AnalystProfileSkeleton from "./AnalystProfileSkeleton";
import "./Analyst.feed.scss";
import {
  AnalystNotFoundButton,
  AnalystNotFoundSection,
  AnalystNotFoundText,
  AnalystNotFoundTitle,
  AnalystProfilePage,
} from "./Analyst.styles";

/**
 * Lovable shell + prod behavior: publishers API, posts/alerts/education previews,
 * see-all navigation with analyst filter, guest alerts gate, Wistia featured video.
 */
const Analyst = () => {
  const { id: slug } = useParams();
  const navigate = useNavigate();
  const goToPricing = useGoToPricing();
  const loggedIn = isLoggedIn();

  const [profile, setProfile] = useState(undefined);
  const [posts, setPosts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [educationalPosts, setEducationalPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(true);

  useEffect(() => {
    scrollPageToTop();
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setProfile(undefined);

      try {
        const publishersResponse = await getPublishers();
        const matchedProfile = (publishersResponse?.data || []).find(
          (publisher) => publisher.slug === slug
        );

        if (!matchedProfile) {
          if (!cancelled) {
            setProfile(null);
            setPosts([]);
            setAlerts([]);
            setEducationalPosts([]);
          }
          return;
        }

        const user = localStorage.getItem("user");
        const guest = !user;

        const postsResponse = await getMixedPostsForPublisher(
          matchedProfile.id,
          3,
          0
        );

        let alertItems = [];
        if (user) {
          const alertsResponse = await getMemberAlerts(
            matchedProfile.id,
            "*",
            3,
            0
          );
          alertItems = alertsResponse?.data?.items || [];
        }

        const educationResponse = await getEducationalPosts(
          {
            authorIds: [matchedProfile.id],
            tagIds: [],
            educationOnly: true,
            searchString: "",
          },
          3,
          0
        );

        if (!cancelled) {
          setProfile(matchedProfile);
          setPosts(postsResponse?.data?.items || []);
          setAlerts(alertItems);
          setEducationalPosts(educationResponse?.data?.items || []);
          setIsGuest(guest);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const showRenewalNotice = useMemo(() => {
    const visibleAlerts = alerts.filter((alert) => alert.content !== undefined);
    return visibleAlerts.length === 0 && alerts.length > 0;
  }, [alerts]);

  const seeAllPosts = useCallback(() => {
    if (!profile) return;
    navigate("/blogs/premium", { state: { analyst: profile.id } });
  }, [navigate, profile]);

  const seeAllAlerts = useCallback(() => {
    if (!profile) return;
    navigate("/alerts", { state: { analyst: profile.id } });
  }, [navigate, profile]);

  const seeAllEducation = useCallback(() => {
    if (!profile) return;
    navigate("/education", { state: { analyst: profile.id } });
  }, [navigate, profile]);

  return (
    <AnalystProfilePage className="analyst-profile-page">
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      {loading && (
        <section style={{ padding: "96px 16px 48px", maxWidth: "80rem", margin: "0 auto" }}>
          <AnalystProfileSkeleton />
        </section>
      )}

      {!loading && profile === null && (
        <AnalystNotFoundSection>
          <AnalystNotFoundTitle>Analyst not found</AnalystNotFoundTitle>
          <AnalystNotFoundText>
            The analyst profile you are looking for does not exist.
          </AnalystNotFoundText>
          <AnalystNotFoundButton as={Link} to="/analysts">
            Back to analysts
          </AnalystNotFoundButton>
        </AnalystNotFoundSection>
      )}

      {!loading && profile && (
        <AnalystProfileContent
          profile={profile}
          posts={posts}
          alerts={alerts}
          educationalPosts={educationalPosts}
          isGuest={isGuest}
          showRenewalNotice={showRenewalNotice}
          onSeeAllPosts={seeAllPosts}
          onSeeAllAlerts={seeAllAlerts}
          onSeeAllEducation={seeAllEducation}
        />
      )}

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />
    </AnalystProfilePage>
  );
};

export default Analyst;
